import React from 'react';
import { 
  Shield, 
  Scale, 
  AlertTriangle, 
  FileText, 
  Globe, 
  Lock,
  Users,
  Gavel,
  Clock,
  CheckCircle
} from 'lucide-react';
import BackButton from '../components/BackButton';

export default function TermsOfUse() {
  const lastUpdated = "January 15, 2024";

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: CheckCircle,
      content: [
        'By accessing and using the Plypto platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.',
        'If you do not agree to these terms, please do not use our platform.',
        'We reserve the right to modify these terms at any time. Continued use of the platform constitutes acceptance of any changes.'
      ]
    },
    {
      id: 'platform-description',
      title: 'Platform Description',
      icon: Globe,
      content: [
        'Plypto is a decentralized prediction platform that allows users to make predictions on cryptocurrency price movements.',
        'The platform operates on blockchain technology and uses smart contracts to facilitate predictions and distribute rewards.',
        'Users can earn PLYP tokens through successful predictions and various platform activities.'
      ]
    },
    {
      id: 'user-responsibilities',
      title: 'User Responsibilities',
      icon: Users,
      content: [
        'You must be at least 18 years old and legally capable of entering into contracts in your jurisdiction.',
        'You are responsible for maintaining the security of your wallet and private keys.',
        'You must comply with all applicable laws and regulations in your jurisdiction.',
        'You agree not to use the platform for any illegal or unauthorized purposes.',
        'You are solely responsible for any taxes that may apply to your activities on the platform.'
      ]
    },
    {
      id: 'risks-disclaimers',
      title: 'Risks and Disclaimers',
      icon: AlertTriangle,
      content: [
        'Cryptocurrency predictions involve significant financial risk. You may lose all funds you commit to predictions.',
        'Past performance does not guarantee future results. All predictions are speculative in nature.',
        'The platform is provided "as is" without warranties of any kind, express or implied.',
        'We do not guarantee the accuracy, completeness, or timeliness of any information on the platform.',
        'Blockchain transactions are irreversible. Always verify transaction details before confirming.',
        'Smart contract risks: While our contracts are audited, bugs or vulnerabilities may exist.'
      ]
    },
    {
      id: 'prohibited-activities',
      title: 'Prohibited Activities',
      icon: Shield,
      content: [
        'Market manipulation or attempting to manipulate prediction outcomes.',
        'Using automated systems, bots, or scripts without explicit permission.',
        'Attempting to exploit bugs, vulnerabilities, or unintended features.',
        'Creating multiple accounts to circumvent platform limits.',
        'Engaging in money laundering or other illegal financial activities.',
        'Violating any applicable laws or regulations.',
        'Interfering with the platform\'s operation or other users\' experience.'
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      icon: Lock,
      content: [
        'All content, trademarks, and intellectual property on the platform are owned by Plypto or its licensors.',
        'You may not copy, modify, distribute, or create derivative works without explicit permission.',
        'The Plypto name, logo, and branding are protected trademarks.',
        'User-generated content remains your property, but you grant us a license to use it on the platform.'
      ]
    },
    {
      id: 'limitation-liability',
      title: 'Limitation of Liability',
      icon: Scale,
      content: [
        'To the maximum extent permitted by law, Plypto shall not be liable for any indirect, incidental, or consequential damages.',
        'Our total liability to you shall not exceed the amount you have paid to the platform in the 12 months preceding the claim.',
        'We are not responsible for losses due to user error, forgotten passwords, or compromised wallets.',
        'We are not liable for losses due to blockchain network issues, gas fee fluctuations, or third-party service failures.'
      ]
    },
    {
      id: 'privacy-data',
      title: 'Privacy and Data',
      icon: Lock,
      content: [
        'We collect minimal personal information and prioritize user privacy.',
        'Blockchain transactions are public and permanent by nature.',
        'We do not sell or share personal information with third parties without consent.',
        'You have the right to request deletion of personal data where applicable.',
        'We use industry-standard security measures to protect user data.'
      ]
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: Gavel,
      content: [
        'We reserve the right to suspend or terminate accounts that violate these terms.',
        'You may stop using the platform at any time, but these terms will continue to apply to past activities.',
        'Upon termination, you remain responsible for any outstanding obligations.',
        'Certain provisions of these terms will survive termination, including liability limitations and intellectual property rights.'
      ]
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      icon: FileText,
      content: [
        'These terms are governed by the laws of [Jurisdiction], without regard to conflict of law principles.',
        'Any disputes will be resolved through binding arbitration in [Jurisdiction].',
        'If any provision of these terms is found to be unenforceable, the remaining provisions will remain in effect.',
        'These terms constitute the entire agreement between you and Plypto regarding the platform.'
      ]
    }
  ];

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Terms of Use</h1>
                <p className="text-gray-400">Please read these terms carefully before using Plypto</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Table of Contents */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10 sticky top-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <FileText className="w-5 h-5 text-orange-400" />
                <span>Contents</span>
              </h3>
              
              <nav className="space-y-2">
                {sections.map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors py-2 px-3 rounded-lg hover:bg-white/10"
                  >
                    <section.icon className="w-4 h-4" />
                    <span className="text-sm">{section.title}</span>
                  </a>
                ))}
              </nav>

              {/* Last Updated */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center space-x-2 text-gray-400 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Last Updated</span>
                </div>
                <p className="text-white text-sm mt-1">{lastUpdated}</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            
            {/* Important Notice */}
            <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-xl p-6 border border-red-500/20">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Important Legal Notice</h3>
                  <p className="text-gray-300 leading-relaxed">
                    These Terms of Use constitute a legally binding agreement. By using Plypto, you agree to these terms. 
                    Cryptocurrency predictions involve significant financial risk. Please read all sections carefully and 
                    consult with legal and financial advisors if needed.
                  </p>
                </div>
              </div>
            </div>

            {/* Terms Sections */}
            {sections.map(section => (
              <section key={section.id} id={section.id} className="bg-white/5 rounded-xl p-8 border border-white/10">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">{section.title}</h2>
                </div>
                
                <div className="space-y-4">
                  {section.content.map((paragraph, index) => (
                    <p key={index} className="text-gray-300 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-8 border border-blue-500/20">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">Questions About These Terms?</h3>
                <p className="text-gray-400 mb-6">
                  If you have any questions about these Terms of Use, please contact our legal team.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors">
                    <FileText className="w-4 h-4" />
                    <span>Contact Legal Team</span>
                  </button>
                  <button className="flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg transition-colors border border-white/20">
                    <Shield className="w-4 h-4" />
                    <span>Privacy Policy</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Acknowledgment</h3>
                  <p className="text-gray-400 mt-1">
                    By using Plypto, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
